import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { motion, Variants } from 'framer-motion';
import { Check, Clock, Shield, X, Zap } from 'lucide-react';
import { useState } from 'react';

import { Footer } from '@/components/Footer';
import { Navbar } from '@/components/Navbar';
import { useSession } from '@/hooks/authHooks';

// Animation variants for scroll-triggered animations
const fadeUpVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

const staggerContainer: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 },
  },
};

const scaleUpVariants: Variants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

interface PricingTier {
  id: 'free' | 'pro' | 'premium';
  name: string;
  description: string;
  price: number;
  currency: string;
  period: string;
  trialDays?: number;
  cta: string;
  highlighted: boolean;
  features: {
    name: string;
    included: boolean;
  }[];
}

const pricingTiers: PricingTier[] = [
  {
    id: 'free',
    name: 'Gratuit',
    description: 'Primul pas spre investiții mai informate',
    price: 0,
    currency: 'RON',
    period: '/lună',
    cta: 'Începe acum',
    highlighted: false,
    features: [
      { name: '1 alertă AI', included: true },
      { name: '10 notificări email', included: true },
      { name: 'Agregare de știri și rapoarte financiare', included: true },
      { name: 'Acces la grafice de piață', included: true },
      { name: 'Istoricul complet al rezultatelor AI', included: false },
      { name: 'Suport prioritar', included: false },
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Pentru investitori care nu lasă nimic la voia întâmplării',
    price: 29,
    currency: 'RON',
    period: '/lună',
    trialDays: 14,
    cta: 'Încearcă 14 zile gratuit',
    highlighted: true,
    features: [
      { name: '3 alerte AI', included: true },
      { name: 'Notificări email nelimitate', included: true },
      { name: 'Agregare de știri și rapoarte financiare', included: true },
      { name: 'Acces la grafice de piață', included: true },
      { name: 'Istoricul complet al rezultatelor AI', included: true },
      { name: 'Suport prioritar', included: false },
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    description: 'Pentru profesioniști care nu fac compromisuri',
    price: 99,
    currency: 'RON',
    period: '/lună',
    trialDays: 30,
    cta: 'Încearcă 30 zile gratuit',
    highlighted: false,
    features: [
      { name: '20 alerte AI', included: true },
      { name: 'Notificări email nelimitate', included: true },
      { name: 'Agregare de știri și rapoarte financiare', included: true },
      { name: 'Acces la grafice de piață', included: true },
      { name: 'Istoricul complet al rezultatelor AI', included: true },
      { name: 'Suport prioritar 24/7', included: true },
    ],
  },
];

const testimonials = [
  {
    name: 'Aurelian M.',
    role: 'Investitor Independent',
    quote:
      'Cu SolomonQ, am observat din timp o mișcare mai mare pe BVB, înainte să devină evidentă pentru toată lumea. Nu zic că e garantat de fiecare dată, dar în cazul ăsta m-a ajutat să evit o parte din pierderi.',
    image: '/testimonials/aurelian.png',
    rating: 5,
  },
  {
    name: 'Ciprian T.',
    role: 'Trader',
    quote:
      'De când folosesc alertele AI, reacționez mai repede și pierd mai puțin timp pe știri. Îmi arată direct ce e relevant pentru mine.',
    image: '/testimonials/ciprian.png',
    rating: 5,
  },
  {
    name: 'Cristina B.',
    role: 'Manager de Portofoliu',
    quote:
      'Alertele AI îmi economisesc timp pentru că nu mai trebuie să verific constant știrile.',
    image: '/testimonials/cristina.png',
    rating: 4,
  },
];

const faqs = [
  {
    question: 'Pot să-mi anulez abonamentul oricând?',
    answer:
      'Îți poți anula abonamentul în orice moment din setări. Fără taxe de anulare și fără condiții.',
  },
  {
    question: 'Ce se întâmplă cu datele mele dacă îmi anulez abonamentul?',
    answer:
      'Datele tale rămân în siguranță. Poți să-ți reactivezi abonamentul oricând și să-ți accesezi din nou alertele și istoricul. Sau poți să ne ceri să-ți ștergem datele de la noi.',
  },
  {
    question: 'Care este diferența dintre alertele Pro și Premium?',
    answer:
      'Pro oferă până la 3 alerte active cu notificări email și 14 zile de trial, în timp ce Premium oferă 20 alerte cu notificări email, 30 zile de trial, plus suport 24/7.',
  },
  {
    question: 'Cum funcționează perioada de trial?',
    answer:
      'Poți testa Pro gratuit 14 zile sau Premium gratuit 30 zile. Nu îți vom cere informații de plată până la finalul perioadei de trial.',
  },
  {
    question: 'Ce se întâmplă dacă nu sunt mulțumit?',
    answer:
      'În perioada de trial nu plătești nimic. După începerea abonamentului plătit, nu oferim rambursări, cu excepția cazurilor în care legea aplicabilă prevede altfel.',
  },
];

export default function UpgradePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session, user } = useSession();
  const isAuthenticated = Boolean(session ?? user);

  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const handleSelectPlan = async (tier: PricingTier['id']) => {
    if (isAuthenticated) {
      // User is already authenticated, update their subscription
      try {
        const response = await fetch('/api/subscription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tier }),
        });

        if (response.ok) {
          // Clear any pending plan from sessionStorage (important for RequireSubscription gate)
          sessionStorage.removeItem('selectedPlan');

          // Invalidate subscription query to fetch fresh data
          await queryClient.invalidateQueries({ queryKey: ['subscription'] });

          // Redirect to appropriate page based on tier
          if (tier === 'free') {
            navigate({ to: '/graphs' });
          } else {
            navigate({ to: '/create-alert' });
          }
        }
      } catch (error) {
        console.error('Error updating subscription:', error);
      }
    } else {
      // Store selected plan in sessionStorage for auth flow (only for unauthenticated users)
      sessionStorage.setItem('selectedPlan', tier);
      // Redirect to auth with plan parameter
      navigate({ to: '/auth/$authFlow', params: { authFlow: 'sign-up' } });
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-neutral-50 text-neutral-800">
      <Navbar />

      <main className="flex-1 pt-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="py-12 text-center md:py-20"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={staggerContainer}>
            <motion.h1
              className="mb-4 font-heading text-4xl font-bold text-neutral-900 md:mb-6 md:text-6xl"
              variants={fadeUpVariants}>
              Fii primul care află
            </motion.h1>
            <motion.p
              className="mx-auto mb-8 max-w-3xl text-base text-neutral-600 md:text-xl"
              variants={fadeUpVariants}>
              Alege planul potrivit pentru tine. Crește-ți veniturile din
              investiții <br className="hidden md:block" /> cu alerte
              inteligente.
            </motion.p>
          </motion.div>

          <motion.div
            className="mb-16 grid grid-cols-1 gap-6 md:mb-20 md:gap-8 lg:grid-cols-3"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}>
            {pricingTiers.map((tier) => (
              <motion.div
                key={tier.id}
                variants={fadeUpVariants}
                className={`relative rounded-2xl transition-all duration-300 ${
                  tier.highlighted
                    ? 'scale-100 bg-white shadow-xl ring-2 ring-green-primary lg:scale-105'
                    : 'border border-neutral-200 bg-white hover:shadow-lg'
                }`}>
                {tier.highlighted && (
                  <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 transform">
                    <span className="rounded-full bg-green-primary px-4 py-1 text-xs font-bold text-white">
                      CEL MAI POPULAR
                    </span>
                  </div>
                )}

                <div className="flex h-full flex-col p-6 md:p-8">
                  <h3 className="mb-2 text-2xl font-bold text-neutral-900">
                    {tier.name}
                  </h3>
                  <p className="mb-6 min-h-[40px] text-sm text-neutral-600">
                    {tier.description}
                  </p>

                  <div className="mb-6 min-h-[100px]">
                    <div className="flex items-baseline gap-1">
                      <span className="text-5xl font-bold text-neutral-900">
                        {tier.price}
                      </span>
                      <span className="text-neutral-600">{tier.currency}</span>
                    </div>
                    <p className="mt-2 text-sm text-neutral-600">
                      {tier.period}
                    </p>
                    {tier.trialDays && tier.trialDays > 0 && (
                      <p className="mt-1 text-sm font-medium text-primary">
                        {tier.trialDays} zile gratuit
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => handleSelectPlan(tier.id)}
                    className={`mb-8 w-full rounded-lg px-4 py-3 font-semibold transition-all duration-200 ${
                      tier.highlighted
                        ? 'bg-green-primary text-white shadow-lg hover:bg-green-primary/90'
                        : 'bg-neutral-100 text-primary hover:bg-neutral-200'
                    }`}>
                    {tier.cta}
                  </button>

                  <div className="space-y-4">
                    {tier.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        {feature.included ? (
                          <Check
                            size={20}
                            className="mt-0.5 flex-shrink-0 text-green-primary"
                          />
                        ) : (
                          <X
                            size={20}
                            className="mt-0.5 flex-shrink-0 text-neutral-300"
                          />
                        )}
                        <span
                          className={`text-sm ${feature.included ? 'text-neutral-700' : 'text-neutral-400'}`}>
                          {feature.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Demo Video Section */}
          <motion.div
            className="mb-16 md:mb-20"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUpVariants}>
            <h2 className="mb-8 text-center text-4xl font-bold text-neutral-900 md:mb-12 md:text-5xl">
              Descoperă SolomonQ
            </h2>
            <div className="mx-auto max-w-4xl">
              <div className="overflow-hidden rounded-2xl border border-neutral-200 shadow-lg">
                <video
                  src="/solomonq-demo.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full"
                />
              </div>
            </div>
          </motion.div>

          {/* Trust Signals */}
          <motion.div
            className="mb-16 rounded-2xl border border-neutral-200 bg-white p-6 md:mb-20 md:p-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUpVariants}>
            <motion.div
              className="grid grid-cols-1 gap-8 md:grid-cols-3"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={staggerContainer}>
              <motion.div className="flex gap-4" variants={fadeUpVariants}>
                <Zap className="flex-shrink-0 text-green-primary" size={28} />
                <div>
                  <h3 className="mb-2 font-bold text-neutral-900">
                    Alerte Inteligente
                  </h3>
                  <p className="text-sm text-neutral-600">
                    Primești notificări în timp real când ceva relevant se
                    întâmplă legat de acțiunile tale.
                  </p>
                </div>
              </motion.div>
              <motion.div className="flex gap-4" variants={fadeUpVariants}>
                <Shield
                  className="flex-shrink-0 text-green-primary"
                  size={28}
                />
                <div>
                  <h3 className="mb-2 font-bold text-neutral-900">
                    Datele Tale sunt Sigure
                  </h3>
                  <p className="text-sm text-neutral-600">
                    Conformitate GDPR, datele rămân private și nu le vindem
                    către terți.
                  </p>
                </div>
              </motion.div>
              <motion.div className="flex gap-4" variants={fadeUpVariants}>
                <Clock className="flex-shrink-0 text-green-primary" size={28} />
                <div>
                  <h3 className="mb-2 font-bold text-neutral-900">
                    Anulare Oricând
                  </h3>
                  <p className="text-sm text-neutral-600">
                    Fără contract pe termen lung. Anulează-ți abonamentul
                    oricând din setări.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div
            className="mb-16 md:mb-20"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}>
            <motion.h2
              className="mb-8 text-center text-4xl font-bold text-neutral-900 md:mb-16 md:text-5xl"
              variants={fadeUpVariants}>
              Ce spun utilizatorii noștri
            </motion.h2>
            <motion.div
              className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={staggerContainer}>
              {testimonials.map((testimonial, idx) => (
                <motion.div
                  key={idx}
                  variants={fadeUpVariants}
                  className="flex flex-col rounded-xl border border-neutral-200 bg-white p-6 md:p-8">
                  <div className="mb-4 flex gap-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-yellow-400">
                        ★
                      </span>
                    ))}
                  </div>
                  <p className="mb-6 italic text-neutral-700">
                    &ldquo;{testimonial.quote}&rdquo;
                  </p>
                  <div className="mt-auto flex items-center gap-3">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-semibold text-neutral-900">
                        {testimonial.name}
                      </p>
                      <p className="text-sm text-neutral-600">
                        {testimonial.role}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* FAQ Section */}
          <motion.div
            className="mb-16 md:mb-20"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}>
            <motion.h2
              className="mb-8 text-center text-4xl font-bold text-neutral-900 md:mb-12 md:text-5xl"
              variants={fadeUpVariants}>
              Întrebări frecvente
            </motion.h2>
            <motion.div
              className="mx-auto max-w-3xl space-y-4"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={staggerContainer}>
              {faqs.map((faq, idx) => (
                <motion.div
                  key={idx}
                  variants={fadeUpVariants}
                  className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
                  <button
                    onClick={() =>
                      setExpandedFaq(expandedFaq === idx ? null : idx)
                    }
                    className="flex w-full items-center justify-between px-4 py-4 transition hover:bg-neutral-50 md:px-6">
                    <span className="text-left text-sm font-semibold text-neutral-900 md:text-base">
                      {faq.question}
                    </span>
                    <span
                      className={`flex-shrink-0 text-neutral-600 transition-transform ${expandedFaq === idx ? 'rotate-180' : ''}`}>
                      ▼
                    </span>
                  </button>
                  {expandedFaq === idx && (
                    <div className="bg-neutral-50 px-4 py-4 md:px-6">
                      <p className="text-sm text-neutral-700 md:text-base">
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            className="relative mb-16 overflow-hidden rounded-2xl p-6 text-center md:mb-20 md:p-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={scaleUpVariants}
            style={{
              background: `
                radial-gradient(ellipse 45% 90% at 50% 50%, rgba(22, 162, 73, 0.35) 0%, rgba(22, 162, 73, 0.15) 40%, transparent 80%),
                linear-gradient(180deg, #1a1a1a 0%, #0a0a0a 100%)
              `,
            }}>
            {/* Left glassy trapezoid */}
            <div
              className="pointer-events-none absolute inset-y-0 left-0 w-1/3"
              style={{
                background: `linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 50%, transparent 100%)`,
                clipPath: 'polygon(0 0, 100% 15%, 60% 85%, 0 100%)',
              }}
            />
            <div
              className="pointer-events-none absolute inset-y-0 left-0 w-1/4"
              style={{
                background: `linear-gradient(145deg, rgba(255,255,255,0.05) 0%, transparent 60%)`,
                clipPath: 'polygon(0 0, 100% 25%, 40% 75%, 0 100%)',
              }}
            />
            {/* Right glassy trapezoid */}
            <div
              className="pointer-events-none absolute inset-y-0 right-0 w-1/3"
              style={{
                background: `linear-gradient(-135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 50%, transparent 100%)`,
                clipPath: 'polygon(0 15%, 100% 0, 100% 100%, 40% 85%)',
              }}
            />
            <div
              className="pointer-events-none absolute inset-y-0 right-0 w-1/4"
              style={{
                background: `linear-gradient(-145deg, rgba(255,255,255,0.05) 0%, transparent 60%)`,
                clipPath: 'polygon(0 25%, 100% 0, 100% 100%, 60% 75%)',
              }}
            />
            {/* Subtle top highlight line */}
            <div
              className="pointer-events-none absolute left-1/4 right-1/4 top-0 h-px"
              style={{
                background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)`,
              }}
            />
            <h2 className="relative mb-3 text-3xl font-bold text-white md:mb-4 md:text-4xl">
              Gata să-ți transformi investițiile?
            </h2>
            <p className="relative mx-auto mb-6 max-w-2xl text-base text-white/80 md:mb-8 md:text-lg">
              Alege-ți planul și începe să creezi alerte AI astazi. Suntem aici
              să te susținem.
            </p>
            <div className="relative flex flex-col justify-center gap-4 sm:flex-row">
              <button
                onClick={() => handleSelectPlan('free')}
                className="rounded-lg bg-white px-6 py-3 font-semibold text-primary transition hover:bg-gray-100 md:px-8 md:py-4">
                Începe cu Gratuit
              </button>
              <button
                onClick={() => handleSelectPlan('pro')}
                className="rounded-lg bg-white px-6 py-3 font-semibold text-primary transition hover:bg-gray-100 md:px-8 md:py-4">
                Încearcă Pro Gratuit
              </button>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
